// src/auth/identityStore.ts
import { v4 as uuidv4 } from "uuid";
import * as OTPAuth from "otpauth";
import { getLegacyFlagsForRole, getRolePermissions, normalizeUserStatus } from "./access";
import { buildDefaultAssignments } from "./accessGrants";
import { dispatchSessionExpired } from "./authSession";
import { getSupabaseClient } from "../lib/supabase";
import type { LegacyAccessFlag, LegacyUserStatus, Locale, Permission, Role, User } from "./types";

export type { Locale, User } from "./types";
export type UserStatus = LegacyUserStatus;

const LS_KEY = "xentros.identity.v1";
const USERS_TABLE = "xentros_users";

type Store = { users: User[] };
type StoreBackend = "local" | "supabase" | "server";

type IdentityUserRow = {
  id: string;
  email: string;
  password: string;
  role: Role | null;
  roles: LegacyAccessFlag[] | null;
  status: LegacyUserStatus | null;
  permissions: Permission[] | null;
  mapped_role: string | null;
  full_access: boolean | null;
  tenant: string | null;
  auth_provider: User["authProvider"] | null;
  mfa_enabled: boolean | null;
  totp_secret: string | null;
  locale: Locale | null;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  phone: string | null;
  department: string | null;
  notes: string | null;
};

type ServerIdentityUserRole = {
  id: string;
  name: Role;
};

type ServerIdentityUserRow = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  isActive: boolean;
  roles: ServerIdentityUserRole[];
  primaryRole?: Role | null;
  full_access?: boolean | null;
  lastLoginAt?: string | null;
  tenantId?: string | null;
};

type ServerUsersEnvelope = {
  ok: boolean;
  users?: ServerIdentityUserRow[];
  user?: ServerIdentityUserRow;
  error?: string;
};

let cachedUsers: User[] | null = null;
let loadPromise: Promise<User[]> | null = null;

function getBackend(): StoreBackend {
  return getSupabaseClient() ? "supabase" : "local";
}

function normalizeUser(user: User): User {
  // ── Derive property context purely from the user record ──────────────────
  // Permissions are resolved server-side; do NOT overlay localStorage here.
  const normalizedEmail = user.email.trim().toLowerCase();
  const propertyIds = user.propertyIds ?? (user.tenant ? [user.tenant] : []);
  const propertyAccess = user.propertyAccess ?? buildDefaultAssignments(user.role, propertyIds);
  const permissions = user.permissions?.length
    ? user.permissions
    : getRolePermissions(user.role);

  return {
    ...user,
    email: normalizedEmail,
    status: normalizeUserStatus(user.status),
    roles: user.roles.length ? user.roles : getLegacyFlagsForRole(user.role),
    permissions,
    locale: user.locale ?? "de",
    authProvider: user.authProvider ?? "local",
    password: user.password ?? "",
    totpSecret: user.totpSecret ?? "",
    mfaEnabled: Boolean(user.mfaEnabled),
    propertyIds,
    propertyAccess,
  };
}

function ensureSystemUsers(users: User[]) {
  // HARDENED: Removed automatic seeding of hardcoded admin accounts.
  // All users must be provisioned through USM.
  // No default fallback admin accounts are created.
  return users.map(normalizeUser);
}

function normalizeStore(store: Store): Store {
  return {
    users: ensureSystemUsers(store.users),
  };
}

function seed(): Store {
  // HARDENED: No hardcoded seed users. Authentication must use configured providers.
  return {
    users: [],
  };
}

function loadLocalStoreSync(): Store {
  const raw = localStorage.getItem(LS_KEY);
  if (!raw) {
    const store = normalizeStore(seed());
    localStorage.setItem(LS_KEY, JSON.stringify(store));
    return store;
  }

  try {
    const store = normalizeStore(JSON.parse(raw) as Store);
    localStorage.setItem(LS_KEY, JSON.stringify(store));
    return store;
  } catch {
    const store = normalizeStore(seed());
    localStorage.setItem(LS_KEY, JSON.stringify(store));
    return store;
  }
}

function saveLocalStore(store: Store) {
  localStorage.setItem(LS_KEY, JSON.stringify(normalizeStore(store)));
}

function emitIdentityUsersChange() {
  window.dispatchEvent(new CustomEvent("xentros-identity-users-change"));
}

function mapRowToUser(row: IdentityUserRow): User {
  return normalizeUser({
    id: row.id,
    email: row.email,
    password: row.password ?? "",
    role: row.role ?? undefined,
    roles: row.roles ?? [],
    status: row.status ?? "active",
    permissions: row.permissions ?? [],
    mappedRole: row.mapped_role ?? undefined,
    fullAccess: row.full_access ?? false,
    tenant: row.tenant ?? undefined,
    authProvider: row.auth_provider ?? undefined,
    mfaEnabled: row.mfa_enabled ?? false,
    totpSecret: row.totp_secret ?? "",
    locale: row.locale ?? "de",
    firstName: row.first_name ?? undefined,
    lastName: row.last_name ?? undefined,
    displayName: row.display_name ?? undefined,
    phone: row.phone ?? undefined,
    department: row.department ?? undefined,
    notes: row.notes ?? undefined,
  });
}

function mapUserToRow(user: User): IdentityUserRow {
  const normalized = normalizeUser(user);
  return {
    id: normalized.id,
    email: normalized.email,
    password: normalized.password,
    role: normalized.role ?? null,
    roles: normalized.roles,
    status: normalized.status ?? "active",
    permissions: normalized.permissions ?? [],
    mapped_role: normalized.mappedRole ?? null,
    full_access: normalized.fullAccess ?? false,
    tenant: normalized.tenant ?? null,
    auth_provider: normalized.authProvider ?? null,
    mfa_enabled: normalized.mfaEnabled,
    totp_secret: normalized.totpSecret,
    locale: normalized.locale,
    first_name: normalized.firstName ?? null,
    last_name: normalized.lastName ?? null,
    display_name: normalized.displayName ?? null,
    phone: normalized.phone ?? null,
    department: normalized.department ?? null,
    notes: normalized.notes ?? null,
  };
}

function mapServerRoleNames(roleNames: string[]) {
  return roleNames.filter((roleName): roleName is Role =>
    [
      "admin",
      "manager",
      "staff",
      "general_manager",
      "front_office",
      "housekeeping",
      "finance",
      "engineering",
      "supervisor",
      "user",
      "guest",
    ].includes(roleName),
  );
}

function mapServerRowToUser(row: ServerIdentityUserRow, existing?: User): User {
  const assignedRoles = mapServerRoleNames((row.roles ?? []).map((role) => role.name));
  const primaryRole = row.primaryRole && assignedRoles.includes(row.primaryRole)
    ? row.primaryRole
    : assignedRoles[0] ?? existing?.role ?? "user";
  const propertyIds = existing?.propertyIds ?? (row.tenantId ? [row.tenantId] : []);

  return normalizeUser({
    id: row.id,
    email: row.email,
    password: existing?.password ?? "",
    role: primaryRole,
    assignedRoles,
    roles: getLegacyFlagsForRole(primaryRole),
    status: row.isActive ? "active" : (existing?.status === "inactive" ? "inactive" : "disabled"),
    permissions: existing?.permissions ?? getRolePermissions(primaryRole),
    mappedRole: primaryRole,
    fullAccess: row.full_access ?? false,
    tenant: row.tenantId ?? existing?.tenant ?? "HQ",
    authProvider: "usm",
    mfaEnabled: existing?.mfaEnabled ?? false,
    totpSecret: existing?.totpSecret ?? "",
    locale: existing?.locale ?? "de",
    firstName: row.firstName ?? existing?.firstName,
    lastName: row.lastName ?? existing?.lastName,
    displayName: row.displayName ?? existing?.displayName,
    phone: existing?.phone,
    department: existing?.department,
    notes: existing?.notes,
    propertyIds,
    propertyAccess: existing?.propertyAccess ?? buildDefaultAssignments(primaryRole, propertyIds),
  });
}

function setCachedUsers(users: User[]) {
  cachedUsers = normalizeStore({ users }).users;
  return cachedUsers;
}

function getCachedUsersSync() {
  if (cachedUsers) {
    return cachedUsers;
  }

  if (getBackend() === "local") {
    return setCachedUsers(loadLocalStoreSync().users);
  }

  return [];
}

async function fetchSupabaseUsers(): Promise<User[]> {
  const client = getSupabaseClient();
  if (!client) {
    return loadLocalStoreSync().users;
  }

  const { data, error } = await client
    .from(USERS_TABLE)
    .select("*")
    .order("email", { ascending: true });

  if (error) {
    throw new Error(`Failed to load identity users from Supabase: ${error.message}`);
  }

  return normalizeStore({ users: (data ?? []).map((row) => mapRowToUser(row as IdentityUserRow)) }).users;
}

async function requestServerIdentity(path: string, init: RequestInit = {}) {
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers ?? {}),
    },
    credentials: "include",
  });

  const payload = await response.json().catch(() => ({})) as ServerUsersEnvelope;
  if (!response.ok || !payload.ok) {
    if (response.status === 401) dispatchSessionExpired();
    throw new Error(payload.error ?? `Identity backend request failed (${response.status}).`);
  }

  return payload;
}

async function fetchServerUsers(): Promise<User[]> {
  const payload = await requestServerIdentity("/api/users");
  const existingById = new Map(getCachedUsersSync().map((user) => [user.id, user]));
  return normalizeStore({
    users: (payload.users ?? []).map((row) => mapServerRowToUser(row, existingById.get(row.id))),
  }).users;
}

async function createServerUser(user: User): Promise<User> {
  const roleName = user.role ?? "user";
  const payload = await requestServerIdentity("/api/users", {
    method: "POST",
    body: JSON.stringify({
      email: user.email,
      firstName: user.firstName ?? null,
      lastName: user.lastName ?? null,
      displayName: user.displayName ?? null,
      isActive: normalizeUserStatus(user.status) === "active",
      roleNames: [roleName],
    }),
  });

  if (!payload.user) {
    throw new Error("Identity backend did not return the created user.");
  }

  return mapServerRowToUser(payload.user, user);
}

async function updateServerUser(userId: string, patch: Partial<User>): Promise<User | null> {
  const existing = getCachedUsersSync().find((user) => user.id === userId);
  if (!existing) return null;

  const merged = normalizeUser({
    ...existing,
    ...patch,
    email: existing.email,
    role: patch.role ?? existing.role,
    roles: patch.role ? getLegacyFlagsForRole(patch.role) : existing.roles,
    status: normalizeUserStatus(patch.status ?? existing.status),
  });

  const payload = await requestServerIdentity(`/api/users/${encodeURIComponent(userId)}`, {
    method: "PATCH",
    body: JSON.stringify({
      firstName: merged.firstName ?? null,
      lastName: merged.lastName ?? null,
      displayName: merged.displayName ?? null,
      isActive: merged.status === "active",
    }),
  });

  if (merged.role) {
    await requestServerIdentity(`/api/users/${encodeURIComponent(userId)}/roles`, {
      method: "PUT",
      body: JSON.stringify({
        roleNames: [merged.role],
      }),
    });
  }

  if (!payload.user) {
    throw new Error("Identity backend did not return the updated user.");
  }

  return mapServerRowToUser({
    ...payload.user,
    roles: merged.role ? [{ id: `${merged.role}`, name: merged.role }] : payload.user.roles,
    primaryRole: merged.role ?? payload.user.primaryRole ?? null,
  }, merged);
}

async function deleteServerUser(userId: string): Promise<User | null> {
  const existing = getCachedUsersSync().find((user) => user.id === userId);
  if (!existing) return null;
  await requestServerIdentity(`/api/users/${encodeURIComponent(userId)}`, {
    method: "DELETE",
  });
  return existing;
}

async function upsertSupabaseUser(user: User): Promise<User> {
  const client = getSupabaseClient();
  if (!client) {
    throw new Error("Supabase client is not configured.");
  }

  const { data, error } = await client
    .from(USERS_TABLE)
    .upsert(mapUserToRow(user), { onConflict: "id" })
    .select()
    .single();

  if (error) {
    throw new Error(`Failed to persist identity user: ${error.message}`);
  }

  return mapRowToUser(data as IdentityUserRow);
}

async function deleteSupabaseUser(userId: string) {
  const client = getSupabaseClient();
  if (!client) {
    throw new Error("Supabase client is not configured.");
  }

  const { error } = await client
    .from(USERS_TABLE)
    .delete()
    .eq("id", userId);

  if (error) {
    throw new Error(`Failed to delete identity user: ${error.message}`);
  }
}

export async function ensureIdentityStoreLoaded(force = false): Promise<User[]> {
  if (!force && cachedUsers) {
    return cachedUsers;
  }

  if (!force && loadPromise) {
    return loadPromise;
  }

  loadPromise = (async () => {
    const backend = getBackend();
    const users = backend === "supabase"
      ? await fetchSupabaseUsers()
      : backend === "server"
        ? await fetchServerUsers()
        : loadLocalStoreSync().users;

    const nextUsers = setCachedUsers(users);
    emitIdentityUsersChange();
    return nextUsers;
  })();

  try {
    return await loadPromise;
  } finally {
    loadPromise = null;
  }
}

export function isUsingSupabaseIdentityStore() {
  return getBackend() === "supabase";
}

export function getUsers(): User[] {
  return getCachedUsersSync();
}

export function getUserById(userId: string): User | undefined {
  return getCachedUsersSync().find((user) => user.id === userId);
}

export function getUserByEmail(email: string): User | undefined {
  const normalizedEmail = email.trim().toLowerCase();
  return getCachedUsersSync().find((user) => user.email === normalizedEmail);
}

export async function createUser(input: Omit<User, "id">): Promise<User> {
  await ensureIdentityStoreLoaded();

  const users = getCachedUsersSync();
  const normalizedEmail = input.email.trim().toLowerCase();
  const existing = users.find((user) => user.email === normalizedEmail);

  if (existing) {
    throw new Error("A user with this email already exists.");
  }

  const newUser = normalizeUser({
    ...input,
    id: `u-${uuidv4()}`,
    email: normalizedEmail,
    status: normalizeUserStatus(input.status),
  });

  if (getBackend() === "supabase") {
    const persistedUser = await upsertSupabaseUser(newUser);
    setCachedUsers([...users, persistedUser]);
    emitIdentityUsersChange();
    return persistedUser;
  }

  if (getBackend() === "server") {
    const persistedUser = await createServerUser(newUser);
    setCachedUsers([...users, persistedUser]);
    emitIdentityUsersChange();
    return persistedUser;
  }

  const nextUsers = [...users, newUser];
  saveLocalStore({ users: nextUsers });
  setCachedUsers(nextUsers);
  emitIdentityUsersChange();
  return newUser;
}

export async function upsertExternalUser(
  input: Omit<User, "id" | "password" | "mfaEnabled" | "totpSecret"> & {
    password?: string;
    mfaEnabled?: boolean;
    totpSecret?: string;
  },
): Promise<User> {
  await ensureIdentityStoreLoaded();

  const users = getCachedUsersSync();
  const normalizedEmail = input.email.trim().toLowerCase();
  const existing = users.find((user) => user.email === normalizedEmail);

  const nextUser = normalizeUser({
    id: existing?.id ?? `u-${uuidv4()}`,
    email: normalizedEmail,
    password: existing?.password ?? input.password ?? "",
    roles: input.roles.length ? input.roles : getLegacyFlagsForRole(input.role),
    role: input.role,
    status: existing?.status ?? normalizeUserStatus(input.status),
    permissions: existing?.permissions ?? input.permissions ?? [],
    mappedRole: input.mappedRole,
    fullAccess: input.fullAccess,
    tenant: input.tenant,
    authProvider: input.authProvider ?? existing?.authProvider ?? "usm",
    mfaEnabled: existing?.mfaEnabled ?? input.mfaEnabled ?? false,
    totpSecret: existing?.totpSecret ?? input.totpSecret ?? "",
    locale: input.locale,
    firstName: input.firstName,
    lastName: input.lastName,
    displayName: input.displayName,
    phone: existing?.phone ?? input.phone,
    department: existing?.department ?? input.department,
    notes: existing?.notes ?? input.notes,
    propertyIds: existing?.propertyIds ?? input.propertyIds ?? (input.tenant ? [input.tenant] : []),
    propertyAccess: existing?.propertyAccess ?? input.propertyAccess ?? buildDefaultAssignments(input.role, input.propertyIds ?? []),
  });

  if (getBackend() === "supabase") {
    const persistedUser = await upsertSupabaseUser(nextUser);
    const nextUsers = existing
      ? users.map((user) => (user.id === existing.id ? persistedUser : user))
      : [...users, persistedUser];
    setCachedUsers(nextUsers);
    emitIdentityUsersChange();
    return persistedUser;
  }

  if (getBackend() === "server") {
    const persistedUser = existing
      ? await updateServerUser(existing.id, nextUser)
      : await createServerUser(nextUser);

    if (!persistedUser) {
      throw new Error("Failed to persist external identity user.");
    }

    const nextUsers = existing
      ? users.map((user) => (user.id === existing.id ? persistedUser : user))
      : [...users, persistedUser];
    setCachedUsers(nextUsers);
    emitIdentityUsersChange();
    return persistedUser;
  }

  const nextUsers = existing
    ? users.map((user) => (user.id === existing.id ? nextUser : user))
    : [...users, nextUser];

  saveLocalStore({ users: nextUsers });
  setCachedUsers(nextUsers);
  emitIdentityUsersChange();
  return nextUser;
}

export function updateUserLocale(userId: string, locale: Locale) {
  const existing = getUserById(userId);
  if (!existing) return;

  const nextUser = normalizeUser({ ...existing, locale });
  const nextUsers = getCachedUsersSync().map((user) => (user.id === userId ? nextUser : user));
  setCachedUsers(nextUsers);
  emitIdentityUsersChange();

  if (getBackend() === "supabase") {
    void upsertSupabaseUser(nextUser).catch((error) => {
      console.error("Failed to persist user locale", error);
    });
    return;
  }

  if (getBackend() === "server") {
    void updateServerUser(userId, { locale }).catch((error) => {
      console.error("Failed to persist user locale", error);
    });
    return;
  }

  saveLocalStore({ users: nextUsers });
}

export async function updateUserStatus(userId: string, status: UserStatus): Promise<User | null> {
  return updateUser(userId, { status });
}

export async function updateUser(userId: string, patch: Partial<User>): Promise<User | null> {
  await ensureIdentityStoreLoaded();

  const users = getCachedUsersSync();
  const existing = users.find((user) => user.id === userId);
  if (!existing) return null;

  const nextRole = patch.role ?? existing.role;
  const updated = normalizeUser({
    ...existing,
    ...patch,
    email: existing.email,
    role: nextRole,
    roles: patch.role ? getLegacyFlagsForRole(nextRole) : existing.roles,
    status: normalizeUserStatus(patch.status ?? existing.status),
  });

  if (getBackend() === "supabase") {
    const persistedUser = await upsertSupabaseUser(updated);
    setCachedUsers(users.map((user) => (user.id === userId ? persistedUser : user)));
    emitIdentityUsersChange();
    return persistedUser;
  }

  if (getBackend() === "server") {
    const persistedUser = await updateServerUser(userId, updated);
    if (!persistedUser) return null;
    setCachedUsers(users.map((user) => (user.id === userId ? persistedUser : user)));
    emitIdentityUsersChange();
    return persistedUser;
  }

  const nextUsers = users.map((user) => (user.id === userId ? updated : user));
  saveLocalStore({ users: nextUsers });
  setCachedUsers(nextUsers);
  emitIdentityUsersChange();
  return updated;
}

export async function deleteUserById(userId: string): Promise<User | null> {
  await ensureIdentityStoreLoaded();

  const users = getCachedUsersSync();
  const existing = users.find((user) => user.id === userId);
  if (!existing) return null;

  if (getBackend() === "supabase") {
    await deleteSupabaseUser(userId);
    setCachedUsers(users.filter((user) => user.id !== userId));
    emitIdentityUsersChange();
    return existing;
  }

  if (getBackend() === "server") {
    await deleteServerUser(userId);
    setCachedUsers(users.filter((user) => user.id !== userId));
    emitIdentityUsersChange();
    return existing;
  }

  const nextUsers = users.filter((user) => user.id !== userId);
  saveLocalStore({ users: nextUsers });
  setCachedUsers(nextUsers);
  emitIdentityUsersChange();
  return existing;
}

export async function verifyPassword(email: string, password: string): Promise<User | null> {
  await ensureIdentityStoreLoaded();
  const user = getUserByEmail(email);
  if (!user) return null;
  if (user.password !== password) return null;
  return user;
}

export function verifyTotp(user: User, code: string): boolean {
  const totp = new OTPAuth.TOTP({
    issuer: "Xentros HOS",
    label: user.email,
    algorithm: "SHA1",
    digits: 6,
    period: 30,
    secret: OTPAuth.Secret.fromBase32(user.totpSecret),
  });

  return totp.validate({ token: code.trim(), window: 1 }) !== null;
}

export function getOtpAuthUri(user: User): string {
  const totp = new OTPAuth.TOTP({
    issuer: "Xentros HOS",
    label: user.email,
    algorithm: "SHA1",
    digits: 6,
    period: 30,
    secret: OTPAuth.Secret.fromBase32(user.totpSecret),
  });

  return totp.toString();
}
